import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowUpRight, Check, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { COURSES, scrollToId, waLink, enquireAboutCourse } from "@/lib/site";

function CourseDialog({ course, onClose }) {
  const wa = waLink(`Hello Motion Mind Institute, I am interested in the ${course.title} course. Please share the details.`);
  return (
    <Dialog open={!!course} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto bg-obsidian-card border-white/10 rounded-3xl p-0 gap-0"
        data-testid="course-detail-dialog"
      >
        {course && (
          <>
            <div className="relative h-44">
              <img src={course.image} alt={course.alt} className="h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian-card via-obsidian-card/40 to-transparent" />
              <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between">
                <DialogHeader className="space-y-0">
                  <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
                    Course {course.num}
                  </DialogDescription>
                  <DialogTitle className="font-display text-2xl font-bold text-white">{course.title}</DialogTitle>
                </DialogHeader>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-sm text-gray-400 leading-relaxed">{course.desc}</p>
              <h4 className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">What you will learn</h4>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                {course.topics.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex items-center justify-between rounded-2xl glass px-5 py-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-400">Fee</span>
                <span className="font-semibold text-brand" data-testid="dialog-course-fee">
                  Enquire Now
                </span>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    onClose();
                    enquireAboutCourse(course.title);
                  }}
                  className="flex-1 rounded-full bg-amber-500 px-6 py-3.5 text-sm font-semibold text-obsidian hover:bg-amber-400 hover:shadow-glow-amber-sm transition-all duration-300"
                  data-testid="dialog-enquire-button"
                >
                  Enquire Now
                </button>
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/40 px-6 py-3.5 text-sm font-medium text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300"
                  data-testid="dialog-whatsapp-button"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Courses() {
  const [active, setActive] = useState(null);
  return (
    <section id="courses" className="relative py-20 md:py-28" data-testid="courses-section">
      <div className="absolute right-0 top-40 -z-10 h-[420px] w-[420px] rounded-full bg-amber-500/[0.06] blur-[130px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Courses"
          title="Explore Our Courses"
          sub="Learn practical creative skills and turn your ideas into professional work."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {COURSES.map((course, i) => (
            <Reveal key={course.id} delay={i * 0.1} className="h-full">
              <motion.article
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/[0.07] bg-obsidian-card hover:border-amber-500/35 transition-colors duration-500"
                data-testid={`course-card-${course.id}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.alt}
                    className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian-card via-obsidian-card/30 to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full glass px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                    Course {course.num}
                  </span>
                  <span className="absolute -bottom-3 right-4 font-display text-6xl font-extrabold text-stroke opacity-70 select-none">
                    {course.num}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6 sm:p-7">
                  <h3 className="font-display text-xl font-bold text-white">{course.title}</h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{course.desc}</p>

                  <ul className="mt-5 space-y-2">
                    {course.topics.slice(0, 4).map((t) => (
                      <li key={t} className="flex items-center gap-2.5 text-[13px] text-gray-300">
                        <Check className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                        {t}
                      </li>
                    ))}
                    <li className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-500/80 pl-6">
                      + {course.topics.length - 4} more topics
                    </li>
                  </ul>

                  <div className="mt-6 flex items-center justify-between border-t border-white/[0.07] pt-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Fee</span>
                    <span className="font-semibold text-brand" data-testid={`course-fee-${course.id}`}>
                      Enquire Now
                    </span>
                  </div>

                  <button
                    onClick={() => setActive(course)}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3.5 text-sm font-medium text-white hover:border-amber-500/60 hover:text-amber-400 transition-all duration-300"
                    data-testid={`course-view-${course.id}`}
                  >
                    View Course
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <CourseDialog course={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
